/**
 * The two reference lists the create-plan dialog picks from -
 * `/api/v1/package-name-suggestions/` and `/api/v1/package-durations/`.
 *
 * They sit apart from the client form's `catalogService` because they answer
 * in a different shape: the rows come wrapped in a key naming the list, and a
 * duration calls its text `label` rather than `name`. One mapping across both
 * plans would have to know which of those it was reading.
 *
 * Like every other list, each call fetches a fresh Clerk token first - they
 * are short-lived, and holding one in a module would go stale between screens.
 */

import { api, API_ENDPOINTS } from "../../../lib/api";
import type { GetToken } from "../../auth/types/auth.types";
import type { PlanDuration, PlanSuggestion } from "../types/brand.types";

/** Wire shapes, exactly as the backend sends them. */
interface PlanSuggestionsResponse {
  suggestions: PlanSuggestion[];
}

interface PlanDurationsResponse {
  durations: PlanDuration[];
}

const token = async (getToken: GetToken, what: string): Promise<string> => {
  const value = await getToken();
  if (!value) {
    // Not an ApiError: the server was never asked. Calling this without an
    // active session is a bug at the call site, not something to show a user.
    throw new Error(`No active Clerk session - cannot load ${what}.`);
  }
  return value;
};

/**
 * Rows without usable text are dropped rather than shown: an empty option is a
 * dead line in the list, and one the dialog could never submit.
 */
const named = <T>(rows: T[] | undefined, text: (row: T) => string): T[] =>
  (rows ?? []).filter((row) => typeof text(row) === "string" && text(row).trim());

/** The plan names the dialog suggests. */
export async function fetchPlanSuggestions(
  getToken: GetToken,
  signal?: AbortSignal
): Promise<PlanSuggestion[]> {
  const response = await api.get<PlanSuggestionsResponse>(
    API_ENDPOINTS.catalog.planSuggestions,
    { token: await token(getToken, "the plan suggestions"), signal }
  );

  return named(response?.suggestions, (row) => row.name).map((row) => ({
    id: row.id,
    name: row.name.trim(),
  }));
}

/** The lengths a plan can be sold for. */
export async function fetchPlanDurations(
  getToken: GetToken,
  signal?: AbortSignal
): Promise<PlanDuration[]> {
  const response = await api.get<PlanDurationsResponse>(
    API_ENDPOINTS.catalog.planDurations,
    { token: await token(getToken, "the plan durations"), signal }
  );

  return named(response?.durations, (row) => row.label).map((row) => ({
    id: row.id,
    label: row.label.trim(),
  }));
}
